import { useParams, useSearchParams } from 'react-router-dom'
import React from 'react'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import Divider from '@mui/material/Divider'
import ArticleIcon from '@mui/icons-material/Article'

import { useQuery, useCommand } from '~/client/hooks/cqrpc'
import ErrorMessage from '~/client/components/ErrorMessage'
import Link from '~/client/components/Link'
import Heading from '~/client/components/Heading'
import Timestamp from '~/client/components/Timestamp'
import EmptyMessage from '~/client/components/EmptyMessage'
import FullPageLoading from '~/client/components/FullPageLoading'
import FullPageMinimalLayout from '~/client/components/FullPageMinimalLayout'
import AppIconConnectToDataPalIcon from '~/client/components/AppIconConnectToDataPalIcon'
import DocumentCreateForm from '~/client/components/DocumentCreateForm'

export const routeOptions = {
  topLevel: true,
  mustBeUser: true,
}

export default function Page() {
  const { id: appAccountId } = useParams()
  const [searchParams] = useSearchParams()
  const { type, documentId, purpose, returnTo, read, write } = Object.fromEntries(searchParams.entries())
  const query = useQuery('appAccounts.getDocumentRequest', { appAccountId, type, documentId })
  const { appAccount, documentType, documents } = query.result || {}
  const { app } = appAccount || {}
  const shareDocument = useCommand('appAccounts.shareDocument', {
    onSuccess({ documentId }){
      let url = new URL(`https://${app.host}`)
      if (returnTo){
        const [pathname, search] = returnTo.split('?')
        url.pathname = pathname
        if (search) url.search = search
      }
      url.searchParams.set('documentId', documentId)
      window.location = `${url}`
    }
  })
  const share = documentId => {
    shareDocument.call({ appAccountId, documentId, read: read == 1, write: write == 1 })
  }

  if (query.loading) return <FullPageLoading/>
  if (!appAccount) return <div>ERROR: app account not found</div>
  if (!documentType) return <div>ERROR: document type not found</div>
  return <FullPageMinimalLayout>
    <Stack
      sx={{flex: '1 1 20vh'}}
      direction="column"
      justifyContent="flex-end"
    >
      <AppIconConnectToDataPalIcon {...{ app }}/>
      <Box sx={{textAlign: 'center', my: 2}}>
        <Typography varaint="body1">{app.host}</Typography>
        <Typography varaint="body2" sx={{ color: 'grey.600' }}>wants a</Typography>
        <Heading variant="sub" sx={{display: 'block'}}>{documentType.name}</Heading>
      </Box>
    </Stack>
    <Paper sx={{ mx: 'auto', p: 2 }}>
      <Typography varaint="body2" sx={{ color: 'grey.600' }}>PURPOSE</Typography>
      <Box>{purpose}</Box>
      <ErrorMessage error={shareDocument.error}/>

      {documentId
        ? <ShareSpecificDocument {...{ document: documents[0], share }}/>
        : <ShareOrCreateDocument {...{ documentType, documents, share }}/>
      }
    </Paper>
    <Box sx={{
      my: 2, border: 1,
      p: 2, borderColor: 'divider', borderRadius: 1, color: 'grey.600',
      fontSize: 'smaller',
    }}>
      Sharing redirects you back to {app.host}
    </Box>
  </FullPageMinimalLayout>
}


function ShareSpecificDocument({ document, share }){
  return <>
    <DataList documents={[document]} share={share}/>
  </>
}

function ShareOrCreateDocument({ documentType, documents, share }){
  const [creatingANewDocument, setCreatingANewDocument] = React.useState(false)
  const [successful, setSuccessful] = React.useState(false)
  return <>
    {documents.length > 0
      ? <>
        <Divider sx={{mt: 3, color: 'grey.400'}}>SHARE EXISTING</Divider>
        <DocumentsList {...{documents, share}}/>
        <Divider sx={{mt: 3, color: 'grey.400'}}>OR</Divider>
      </>
      : null
    }
    {creatingNewDocument
      ? <DocumentCreateForm {...{
        disabled: successful,
        documentTypeId: documentType.id,
        submitText: 'Create and share',
        submittingText: 'Creating and sharing…',
        onSuccess({ id }){
          setSuccessful(true)
          share(id)
        }
      }}/>
      : <Box sx={{mt: 2}}>
        <Button
          variant="contained"
          onClick={() => { setCreatingANewDocument(true) }}
          fullWidth
        >Create a new {documentType.name}</Button>
      </Box>
    }
  </>
}

function DataList({ documents, share }){
  return documents.length === 0
    ? <EmptyMessage>You dont have any</EmptyMessage>
    : <List sx={{m: 0, p: 0}}>
      {documents.map(doc =>
        <ListItem key={doc.id}>
          <ListItemIcon><ArticleIcon/></ListItemIcon>
          <ListItemText
            primary={<Link newWindow to={`/data/${doc.id}`}>{doc.name}</Link>}
            secondary={<Timestamp at={doc.createdAt}/>}
          />
          <Button
            variant="contained"
            sx={{ml: 2}}
            onClick={() => { share(doc.id) }}
          >SHARE</Button>
        </ListItem>
      )}
    </List>
}
